import { odata } from "@/lib/odata";

export interface Paged<T> {
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  items: T[];
}

export interface SortClause {
  by: string;
  dir?: "asc" | "desc";
}

export interface PageParams {
  pageNumber?: number;
  pageSize?: number;
  keyword?: string;
  status?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  /** Multi-column sort, emitted as `sort=a asc, b desc`. Takes precedence over `sortBy`/`sortDir`. */
  sort?: SortClause[];
  search?: Record<string, string | undefined | null>;
  filter?: Record<string, string | undefined | null>;
  /** Pre-built raw OData clauses (use `odata.*` to build them) AND-combined into `filter`. */
  expr?: string[];
}

export interface StatusSummary {
  total: number;
  byStatus: Record<string, number>;
}

export function toStatusSummary(res: unknown): StatusSummary {
  const r = (res ?? {}) as { total?: unknown; byStatus?: Record<string, unknown> };
  const byStatus: Record<string, number> = {};
  for (const [k, v] of Object.entries(r.byStatus ?? {})) byStatus[k] = Number(v) || 0;
  return { total: Number(r.total) || 0, byStatus };
}

export const DEFAULT_PAGE_SIZE = 20;

export function toPaged<T>(
  res: Paged<T> | T[] | null | undefined,
  fallbackPageSize = DEFAULT_PAGE_SIZE,
): Paged<T> {
  if (Array.isArray(res)) {
    return {
      pageNumber: 1,
      pageSize: res.length || fallbackPageSize,
      totalCount: res.length,
      totalPages: 1,
      hasPreviousPage: false,
      hasNextPage: false,
      items: res,
    };
  }
  if (res && Array.isArray((res as Paged<T>).items)) return res as Paged<T>;
  return emptyPage<T>(fallbackPageSize);
}

// Empty page — handy as an initial state before the first fetch resolves.
export function emptyPage<T>(pageSize = DEFAULT_PAGE_SIZE): Paged<T> {
  return {
    pageNumber: 1,
    pageSize,
    totalCount: 0,
    totalPages: 0,
    hasPreviousPage: false,
    hasNextPage: false,
    items: [],
  };
}

export const LIST_ALL_PAGE_SIZE = 200;
export const LIST_ALL_MAX_PAGES = 10;

export async function fetchAll<T, P extends PageParams = PageParams>(
  fetchPage: (params: P) => Promise<Paged<T>>,
  params: P = {} as P,
  label = "list",
): Promise<T[]> {
  const pageSize = params.pageSize ?? LIST_ALL_PAGE_SIZE;
  const items: T[] = [];
  let pageNumber = params.pageNumber ?? 1;

  for (let i = 0; i < LIST_ALL_MAX_PAGES; i += 1) {
    const page = await fetchPage({ ...params, pageNumber, pageSize });
    items.push(...page.items);
    if (!page.hasNextPage || page.items.length === 0) return items;
    pageNumber += 1;
  }

  console.warn(
    `[paging] ${label}: stopped after ${LIST_ALL_MAX_PAGES} pages (${items.length} loaded). ` +
      "Results are truncated — this caller needs server-side search or pagination.",
  );
  return items;
}

export function pageQuery(
  p: PageParams = {},
  extra?: Record<string, string | number | undefined | null>,
  searchColumns?: string[],
): string {
  const sp = new URLSearchParams();
  if (p.pageNumber != null) sp.set("pageNumber", String(p.pageNumber));
  if (p.pageSize != null) sp.set("pageSize", String(p.pageSize));

  const clauses: string[] = [];

  const kw = p.keyword?.trim();
  if (kw && searchColumns?.length) {
    clauses.push(odata.or(...searchColumns.map((col) => odata.contains(col, kw))));
  }
  if (p.status?.trim()) clauses.push(odata.eq("status", p.status.trim()));
  for (const [col, v] of Object.entries(p.search ?? {})) {
    if (v != null && String(v).trim().length > 0)
      clauses.push(odata.contains(col, String(v).trim()));
  }
  for (const [col, v] of Object.entries(p.filter ?? {})) {
    if (v != null && String(v).trim().length > 0)
      clauses.push(odata.eq(col, String(v).trim()));
  }
  for (const c of p.expr ?? []) {
    if (c && c.length > 0) clauses.push(c);
  }

  const filter = odata.and(...clauses);
  if (filter) sp.set("filter", filter);

  const sort = (p.sort ?? []).filter((c) => c.by.trim().length > 0);
  if (sort.length > 0) {
    sp.set("sort", sort.map((c) => `${c.by.trim()} ${c.dir ?? "asc"}`).join(", "));
  } else if (p.sortBy?.trim()) {
    sp.set("sort", `${p.sortBy.trim()} ${p.sortDir ?? "asc"}`);
  }

  for (const [k, v] of Object.entries(extra ?? {})) {
    if (v != null && String(v).length > 0) sp.set(k, String(v));
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}
